<script setup lang="ts" generic="T extends {id: number}">
  import { computed } from 'vue'

  const props = defineProps<{
    data: T[],
    modelValue: number[],
    disabled?: boolean,
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: number[]): void,
  }>()

  const selected = computed({
    set: (value) => {
      emit('update:modelValue', value)
    },
    get: () => props.modelValue,
  })
  const selectedAll = computed({
    set: (value) => {
      if (value) {
        selected.value = props.data.map((data) => data.id)
      } else {
        selected.value = []
      }
    },
    get: () =>
      selected.value.length > 0 && props.data.every((data) => selected.value.includes(data.id)),
  })
  const selectAllIndeterminate = computed(() => {
    const selectedSome = selected.value.length > 0
    const notAllSelected = selected.value.length < props.data.length
    return selectedSome && notAllSelected
  })

  const toggle = (id: number) => {
    if (selected.value.includes(id)) {
      selected.value = selected.value.filter((selectedId) => selectedId !== id)
    } else {
      selected.value = [...selected.value, id]
    }
  }
</script>

<template>
  <app-table class="checkable">
    <thead>
      <th
        v-if="!disabled"
        class="text-center w-px">
        <app-checkbox
          v-model="selectedAll"
          class="table-row-checkbox"
          :indeterminate="selectAllIndeterminate" />
      </th>
      <slot name="thead"></slot>
    </thead>
    <slot name="tbody">
      <tbody>
        <tr
          v-for="(item, index) in data"
          :key="item.id">
          <td
            v-if="!disabled"
            class="w-px">
            <app-checkbox
              v-model="selected"
              class="table-row-checkbox"
              :value="item.id" />
          </td>
          <slot
            :item="item"
            :index="index"
            :toggle="() => toggle(item.id)"></slot>
        </tr>
      </tbody>
    </slot>
  </app-table>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  th:first-child,
  td:first-child {
    @apply px-2;
  }
</style>

<style>
  @reference "@/assets/styles/main.css";

  .table-row-checkbox {
    @apply relative size-8 justify-center rounded-full;

    .checkbox-content {
      @apply hidden;
    }

    /* hover effect */
    input[type="checkbox"]:enabled:hover::before {
      @apply content-empty bg-primary-700 absolute inset-0 block rounded-full opacity-20;
    }
  }
</style>
